// Platform Admin — Governance Gates + Automation Hooks, combined on one page (mirrors the
// mockup's P4 screens). Linked from the Builder's "Governance & Hooks" button. Reuses the same
// governance_gates/automation_hooks arrays and update mutation the Builder round-trips — no new
// data shape, just a dedicated page + modal UX instead of a flat section inside the old editor.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import PageShell from '@/components/Layout/PageShell';
import { Modal } from '@/components/UI/Modal';
import {
  useGetWorkflowDefinitionQuery,
  useUpdateWorkflowDefinitionMutation,
  CreateWorkflowRequest,
  GovernanceGate,
  ApprovalType,
  AutomationHook,
} from '@/redux/internal-services/workflowApi';
import { ROLE_OPTIONS, humanize } from '@/components/Workflow/workflowSettingsShared';
import { useListGovernanceActionsQuery, usePreviewGovernanceOptionsQuery } from '@/redux/internal-services/workflowGovernanceApi';

const Label: React.FC<{ children: React.ReactNode; required?: boolean }> = ({ children, required }) => (
  <label className="block text-xs font-semibold text-ui-text2 mb-1">
    {children}{required && <span className="text-red-400 ml-1">*</span>}
  </label>
);
const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input {...props} className={`w-full px-3 py-2 rounded-lg border border-ui-border2 bg-ui-raised text-sm text-ui-text focus:outline-none focus:ring-2 focus:ring-brand-dark/50 ${props.className ?? ''}`} />
);
const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (props) => (
  <select {...props} className={`w-full px-3 py-2 rounded-lg border border-ui-border2 bg-ui-raised text-sm text-ui-text focus:outline-none focus:ring-2 focus:ring-brand-dark/50 ${props.className ?? ''}`} />
);
const Btn: React.FC<React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'ghost' | 'danger' }> = ({ variant = 'ghost', className = '', ...props }) => {
  const base = 'px-3 py-1.5 text-xs font-medium rounded-lg transition disabled:opacity-50';
  const v = variant === 'primary'
    ? 'bg-brand-dark text-white hover:opacity-90'
    : variant === 'danger'
    ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
    : 'bg-ui-subtle text-ui-text2 hover:bg-ui-hover';
  return <button {...props} className={`${base} ${v} ${className}`} />;
};

const slug = (s: string) => s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

const CONDITION_TYPES: Array<{ value: string; label: string }> = [
  { value: 'required_data', label: 'Required data — field present' },
  { value: 'required_document', label: 'Document — files attached' },
  { value: 'task_completion', label: 'Required task — task complete' },
  { value: 'approval', label: 'Approval — requires sign-off' },
  { value: 'business_validation', label: 'Platform validation — built-in rule' },
];

const TRIGGER_POINTS: Array<{ value: string; label: string }> = [
  { value: 'on_enter', label: 'On enter status' },
  { value: 'on_exit', label: 'On exit status' },
  { value: 'on_dwell', label: 'On dwell (time in status)' },
];

// Values MUST match app.domain.workflows.enums.AutomationActionType exactly — the backend's
// _dispatch_hook only recognizes these 5 and silently records any other value as a failed
// outcome ("Unknown action_type '...'"), non-mandatory hooks swallow that error so the
// transition itself still succeeds, which makes a typo'd value here easy to miss without
// checking workflow_automation_outcomes directly. 'module_action' is accepted but not yet
// backed by any real module — it always records outcome status "skipped_unavailable".
const ACTION_TYPES: Array<{ value: string; label: string }> = [
  { value: 'task_creation', label: 'Create task' },
  { value: 'follow_up', label: 'Create follow-up task' },
  { value: 'notification', label: 'Send notification' },
  { value: 'approval_creation', label: 'Request approval' },
  { value: 'module_action', label: 'Invoke module action (not yet available)' },
];

// ─────────────────────────── Required-data capture + post-action builder ──
// WFL-FR-03-007/008 — "1. What we ask for" (per-field capture spec) + "2. What happens once it
// is answered" (persist is automatic/always-on; the backend action + wait_for_it/fire_and_forget
// split is what's configured here). condition_config.fields stays the list of required field
// keys (unchanged, backward-compatible with older gates); capture/on_captured are additive.

// Both take `t` rather than calling useTranslation() themselves — they're plain functions used
// inside component bodies (a module-level constant can't call a hook).
const inputTypes = (t: (key: string) => string): Array<{ value: string; label: string }> => [
  { value: 'text', label: t('workflow.settings.requiredDataBuilder.inputTypeText') },
  { value: 'number', label: t('workflow.settings.requiredDataBuilder.inputTypeNumber') },
  { value: 'currency', label: t('workflow.settings.requiredDataBuilder.inputTypeCurrency') },
  { value: 'date', label: t('workflow.settings.requiredDataBuilder.inputTypeDate') },
  { value: 'dropdown', label: t('workflow.settings.requiredDataBuilder.inputTypeDropdown') },
  { value: 'multi_select', label: t('workflow.settings.requiredDataBuilder.inputTypeMultiSelect') },
  { value: 'file', label: t('workflow.settings.requiredDataBuilder.inputTypeFile') },
  { value: 'yes_no', label: t('workflow.settings.requiredDataBuilder.inputTypeYesNo') },
];

const optionSources = (t: (key: string) => string): Array<{ value: string; label: string }> => [
  { value: '', label: t('workflow.settings.requiredDataBuilder.optionSourceNotApplicable') },
  { value: 'static', label: t('workflow.settings.requiredDataBuilder.optionSourceFixedList') },
  { value: 'role_lookup', label: t('workflow.settings.requiredDataBuilder.optionSourceRoleLookup') },
];

interface CaptureFieldRow {
  // No separate "field key" input — the mockup only ever shows a Field Label; the technical
  // condition_config.fields key is derived from it via slug() at save time (same pattern this
  // file already uses for gate_code from the condition name).
  label: string;
  input_type: string;
  captured_by_role: string;
  options_source: string;
  options_value: string; // comma-separated options (static) or a role code (role_lookup)
  mandatory: boolean;
  tenant_relabel_allowed: boolean;
}

const emptyCaptureRow = (): CaptureFieldRow => ({
  label: '', input_type: 'text', captured_by_role: '',
  options_source: '', options_value: '', mandatory: true, tenant_relabel_allowed: false,
});

const stepCardClass = (active: boolean) =>
  `text-left px-3 py-2 rounded-lg border transition ${active ? 'border-brand-dark bg-brand-light/40' : 'border-ui-border2 hover:bg-ui-hover'}`;

// "Resolves at runtime to" live preview (WFL-FR-03-007) for a role_lookup options source — never
// a frozen list, re-queried whenever the role selection changes.
const RoleLookupPreview: React.FC<{ role: string }> = ({ role }) => {
  const { t } = useTranslation();
  const { data, isFetching } = usePreviewGovernanceOptionsQuery({ role }, { skip: !role });
  if (!role) return null;
  if (isFetching) return <p className="text-[11px] text-ui-text3">{t('workflow.settings.requiredDataBuilder.resolving')}</p>;
  const preview = data?.data;
  if (!preview) return null;
  if (!preview.available) {
    return (
      <p className="text-[11px] text-ui-text3">
        {t('workflow.settings.requiredDataBuilder.resolvesPerTenantNoPreview')}
      </p>
    );
  }
  const shown = preview.options.slice(0, 3);
  const extra = preview.options.length - shown.length;
  return (
    <div className="rounded-lg border border-ui-border2 bg-ui-subtle p-2">
      <p className="text-[10px] uppercase tracking-wide text-ui-text3 mb-1.5">{t('workflow.settings.requiredDataBuilder.resolvesAtRuntimeTo')}</p>
      {preview.options.length === 0 ? (
        <p className="text-xs text-ui-text3">{t('workflow.settings.requiredDataBuilder.noActiveMembers')}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {shown.map((o) => (
            <span key={o.value} className="px-2 py-0.5 rounded-full bg-ui-raised border border-ui-border2 text-xs text-ui-text2">{o.label}</span>
          ))}
          {extra > 0 && <span className="px-2 py-0.5 rounded-full bg-ui-raised border border-ui-border2 text-xs text-ui-text3">{t('workflow.settings.requiredDataBuilder.extraActive', { count: extra })}</span>}
        </div>
      )}
    </div>
  );
};

const RequiredDataCaptureBuilder: React.FC<{
  captureFields: CaptureFieldRow[];
  setCaptureFields: (rows: CaptureFieldRow[]) => void;
  onCapturedEnabled: boolean;
  setOnCapturedEnabled: (v: boolean) => void;
  actionKey: string;
  setActionKey: (v: string) => void;
  inputMapping: Array<{ param: string; expr: string }>;
  setInputMapping: (rows: Array<{ param: string; expr: string }>) => void;
  mode: 'wait_for_it' | 'fire_and_forget';
  setMode: (v: 'wait_for_it' | 'fire_and_forget') => void;
  onFailure: 'block' | 'warn' | 'ignore';
  setOnFailure: (v: 'block' | 'warn' | 'ignore') => void;
  maxRetries: number;
  setMaxRetries: (v: number) => void;
  backoff: 'exponential' | 'fixed';
  setBackoff: (v: 'exponential' | 'fixed') => void;
}> = ({
  captureFields, setCaptureFields, onCapturedEnabled, setOnCapturedEnabled, actionKey, setActionKey,
  inputMapping, setInputMapping, mode, setMode, onFailure, setOnFailure, maxRetries, setMaxRetries, backoff, setBackoff,
}) => {
  const { t } = useTranslation();
  const { data: actionsData, isLoading: actionsLoading } = useListGovernanceActionsQuery();
  const actions = actionsData?.data ?? [];

  const updateRow = (i: number, patch: Partial<CaptureFieldRow>) =>
    setCaptureFields(captureFields.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-ui-border pt-3">
      {/* ── 1. What we ask for ──────────────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <p className="text-xs font-semibold text-ui-text2">{t('workflow.settings.requiredDataBuilder.whatWeAskForHeading')}</p>
          <p className="text-[10px] uppercase tracking-wide text-ui-text3">{t('workflow.settings.requiredDataBuilder.shownInAppAtGate')}</p>
        </div>
        {captureFields.map((row, i) => (
          <div key={i} className="rounded-lg border border-ui-border2 p-2.5 space-y-2.5">
            <div className="flex items-center justify-between">
              <Label>{t('workflow.settings.requiredDataBuilder.inputTypeLabel')}</Label>
              {captureFields.length > 1 && (
                <button type="button" onClick={() => setCaptureFields(captureFields.filter((_, idx) => idx !== i))} className="text-[11px] text-red-400 hover:underline">
                  {t('workflow.settings.requiredDataBuilder.remove')}
                </button>
              )}
            </div>
            <div className="grid grid-cols-4 gap-1.5">
              {inputTypes(t).map((it) => (
                <button
                  key={it.value}
                  type="button"
                  onClick={() => updateRow(i, { input_type: it.value })}
                  className={`px-1.5 py-1.5 rounded-lg border text-[11px] font-medium transition ${
                    row.input_type === it.value ? 'border-brand-dark bg-brand-light/40 text-brand-dark' : 'border-ui-border2 text-ui-text2 hover:bg-ui-hover'
                  }`}
                >
                  {it.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label required>{t('workflow.settings.requiredDataBuilder.fieldLabel')}</Label>
                <Input value={row.label} onChange={(e) => updateRow(i, { label: e.target.value })} placeholder={t('workflow.settings.requiredDataBuilder.fieldLabelPlaceholder')} />
              </div>
              <div>
                <Label>{t('workflow.settings.requiredDataBuilder.capturedBy')}</Label>
                <Select value={row.captured_by_role} onChange={(e) => updateRow(i, { captured_by_role: e.target.value })}>
                  <option value="">{t('workflow.settings.requiredDataBuilder.anyRole')}</option>
                  {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{humanize(r)}</option>)}
                </Select>
              </div>
            </div>
            {(row.input_type === 'dropdown' || row.input_type === 'multi_select') && (
              <div className="space-y-1.5 border-t border-ui-border2 pt-2">
                <Label>{t('workflow.settings.requiredDataBuilder.optionsSource')}</Label>
                <Select value={row.options_source} onChange={(e) => updateRow(i, { options_source: e.target.value, options_value: '' })}>
                  {optionSources(t).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </Select>
                {row.options_source === 'static' && (
                  <Input value={row.options_value} onChange={(e) => updateRow(i, { options_value: e.target.value })} placeholder={t('workflow.settings.requiredDataBuilder.staticOptionsPlaceholder')} />
                )}
                {row.options_source === 'role_lookup' && (
                  <>
                    <Select value={row.options_value} onChange={(e) => updateRow(i, { options_value: e.target.value })}>
                      <option value="">{t('workflow.settings.requiredDataBuilder.selectRolePlaceholder')}</option>
                      {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{humanize(r)}</option>)}
                    </Select>
                    <RoleLookupPreview role={row.options_value} />
                    <p className="text-[11px] text-ui-text3">{t('workflow.settings.requiredDataBuilder.notFrozenListHint')}</p>
                  </>
                )}
              </div>
            )}
            <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
              <input type="checkbox" checked={row.mandatory} onChange={(e) => updateRow(i, { mandatory: e.target.checked })} /> {t('workflow.settings.requiredDataBuilder.mandatoryToggle')}
            </label>
            <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
              <input type="checkbox" checked={row.tenant_relabel_allowed} onChange={(e) => updateRow(i, { tenant_relabel_allowed: e.target.checked })} /> {t('workflow.settings.requiredDataBuilder.allowRelabelToggle')}
            </label>
          </div>
        ))}
        <Btn onClick={() => setCaptureFields([...captureFields, emptyCaptureRow()])}>{t('workflow.settings.requiredDataBuilder.addField')}</Btn>
      </div>

      {/* ── 2. What happens once it is answered ─────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <p className="text-xs font-semibold text-ui-text2">{t('workflow.settings.requiredDataBuilder.whatHappensHeading')}</p>
          <p className="text-[10px] uppercase tracking-wide text-ui-text3">{t('workflow.settings.requiredDataBuilder.runsInOrder')}</p>
        </div>

        <div className="flex gap-2">
          <span className="shrink-0 w-5 h-5 rounded-full bg-ui-subtle text-ui-text2 text-[11px] font-semibold flex items-center justify-center">1</span>
          <div>
            <p className="text-sm font-medium text-ui-text">{t('workflow.settings.requiredDataBuilder.persistTheValue')}</p>
            <p className="text-[11px] text-ui-text3">{t('workflow.settings.requiredDataBuilder.persistDescription')}</p>
          </div>
        </div>

        <div className="flex gap-2">
          <span className="shrink-0 w-5 h-5 rounded-full bg-ui-subtle text-ui-text2 text-[11px] font-semibold flex items-center justify-center">2</span>
          <div className="flex-1 space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-ui-text cursor-pointer">
              <input type="checkbox" checked={onCapturedEnabled} onChange={(e) => setOnCapturedEnabled(e.target.checked)} /> {t('workflow.settings.requiredDataBuilder.thenRunBackendAction')}
            </label>
            {onCapturedEnabled && (
              <div className="rounded-lg border border-ui-border2 p-2.5 space-y-2">
                <Label required>{t('workflow.settings.requiredDataBuilder.actionRegisteredOnly')}</Label>
                <Select value={actionKey} onChange={(e) => setActionKey(e.target.value)}>
                  <option value="">{actionsLoading ? t('workflow.settings.requiredDataBuilder.loading') : t('workflow.settings.requiredDataBuilder.select')}</option>
                  {actions.map((a) => <option key={a.action_key} value={a.action_key}>{a.action_key}{a.description ? ` — ${a.description}` : ''}</option>)}
                </Select>

                <Label>{t('workflow.settings.requiredDataBuilder.inputMapping')}</Label>
                {inputMapping.map((m, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input value={m.param} onChange={(e) => setInputMapping(inputMapping.map((r, idx) => (idx === i ? { ...r, param: e.target.value } : r)))} placeholder={t('workflow.settings.requiredDataBuilder.paramPlaceholder')} className="flex-1" />
                    <span className="text-ui-text3 text-xs">←</span>
                    <Input value={m.expr} onChange={(e) => setInputMapping(inputMapping.map((r, idx) => (idx === i ? { ...r, expr: e.target.value } : r)))} placeholder={t('workflow.settings.requiredDataBuilder.exprPlaceholder')} className="flex-1" />
                    <button type="button" onClick={() => setInputMapping(inputMapping.filter((_, idx) => idx !== i))} className="text-[11px] text-red-400 hover:underline shrink-0">{t('workflow.settings.requiredDataBuilder.remove')}</button>
                  </div>
                ))}
                <Btn onClick={() => setInputMapping([...inputMapping, { param: '', expr: '' }])}>{t('workflow.settings.requiredDataBuilder.addMapping')}</Btn>
              </div>
            )}
          </div>
        </div>

        {onCapturedEnabled && (
          <div className="flex gap-2">
            <span className="shrink-0 w-5 h-5 rounded-full bg-ui-subtle text-ui-text2 text-[11px] font-semibold flex items-center justify-center">3</span>
            <div className="flex-1 space-y-2">
              <p className="text-sm font-medium text-ui-text">{t('workflow.settings.requiredDataBuilder.handleTheResult')}</p>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" onClick={() => setMode('wait_for_it')} className={stepCardClass(mode === 'wait_for_it')}>
                  <p className="text-xs font-semibold text-ui-text">{t('workflow.settings.requiredDataBuilder.waitForIt')}</p>
                  <p className="text-[11px] text-ui-text3">{t('workflow.settings.requiredDataBuilder.waitForItHint')}</p>
                </button>
                <button type="button" onClick={() => setMode('fire_and_forget')} className={stepCardClass(mode === 'fire_and_forget')}>
                  <p className="text-xs font-semibold text-ui-text">{t('workflow.settings.requiredDataBuilder.fireAndForget')}</p>
                  <p className="text-[11px] text-ui-text3">{t('workflow.settings.requiredDataBuilder.fireAndForgetHint')}</p>
                </button>
              </div>
              {mode === 'wait_for_it' && (
                <div>
                  <Label>{t('workflow.settings.requiredDataBuilder.onFailure')}</Label>
                  <Select value={onFailure} onChange={(e) => setOnFailure(e.target.value as 'block' | 'warn' | 'ignore')}>
                    <option value="block">{t('workflow.settings.requiredDataBuilder.blockAndShowError')}</option>
                    <option value="warn">{t('workflow.settings.requiredDataBuilder.warnAndProceed')}</option>
                    <option value="ignore">{t('workflow.settings.requiredDataBuilder.ignore')}</option>
                  </Select>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <Label>{t('workflow.settings.requiredDataBuilder.retries')}</Label>
                  <Input
                    type="number" min={0} max={5} value={maxRetries}
                    onChange={(e) => setMaxRetries(Math.min(Math.max(Number(e.target.value) || 0, 0), 5))}
                  />
                </div>
                <div className="flex-1">
                  <Label>{t('workflow.settings.requiredDataBuilder.backoff')}</Label>
                  <Select value={backoff} onChange={(e) => setBackoff(e.target.value as 'exponential' | 'fixed')}>
                    <option value="exponential">{t('workflow.settings.requiredDataBuilder.exponential')}</option>
                    <option value="fixed">{t('workflow.settings.requiredDataBuilder.fixed')}</option>
                  </Select>
                </div>
              </div>
              <div className="rounded-lg bg-ui-subtle border border-ui-border2 p-2">
                <p className="text-[10px] uppercase tracking-wide text-ui-text3 mb-1">{t('workflow.settings.requiredDataBuilder.howItIsImplemented')}</p>
                <p className="text-[11px] text-ui-text3">
                  {t('workflow.settings.requiredDataBuilder.howItIsImplementedBody')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────── Add Governance Condition modal ───────────────

const AddGateModal: React.FC<{
  approvalTypes: ApprovalType[];
  onClose: () => void;
  onAdd: (g: GovernanceGate) => void;
}> = ({ approvalTypes, onClose, onAdd }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [targetRef, setTargetRef] = useState('');
  const [conditionType, setConditionType] = useState(CONDITION_TYPES[0].value);
  const [classification, setClassification] = useState<'blocking' | 'warning'>('blocking');
  const [approvalTypeCode, setApprovalTypeCode] = useState('');
  const [approverRole, setApproverRole] = useState('');
  const [prohibitSelfApproval, setProhibitSelfApproval] = useState(false);
  const [failureMessage, setFailureMessage] = useState('');

  const [captureFields, setCaptureFields] = useState<CaptureFieldRow[]>([emptyCaptureRow()]);
  const [onCapturedEnabled, setOnCapturedEnabled] = useState(false);
  const [actionKey, setActionKey] = useState('');
  const [inputMapping, setInputMapping] = useState<Array<{ param: string; expr: string }>>([{ param: '', expr: 'this.value' }]);
  const [mode, setMode] = useState<'wait_for_it' | 'fire_and_forget'>('fire_and_forget');
  const [onFailure, setOnFailure] = useState<'block' | 'warn' | 'ignore'>('block');
  const [maxRetries, setMaxRetries] = useState(3);
  const [backoff, setBackoff] = useState<'exponential' | 'fixed'>('exponential');

  const canSave = name.trim() && targetRef.trim()
    && (conditionType !== 'required_data' || captureFields.some((f) => f.label.trim()));

  const selectApprovalType = (code: string) => {
    setApprovalTypeCode(code);
    const match = approvalTypes.find((a) => a.approval_type_code === code);
    if (match) {
      setApproverRole(match.approver_role);
      setProhibitSelfApproval(!match.self_approval_allowed);
    }
  };

  const save = () => {
    if (!canSave) return;
    let conditionConfig: Record<string, unknown> = {};
    if (conditionType === 'approval') {
      conditionConfig = { approval_type: approvalTypeCode || undefined, approver_role: approverRole || undefined, prohibit_self_approval: prohibitSelfApproval };
    } else if (conditionType === 'required_data') {
      const rows = captureFields.filter((f) => f.label.trim());
      conditionConfig = {
        fields: rows.map((f) => slug(f.label)),
        capture: Object.fromEntries(rows.map((f) => [slug(f.label), {
          input_type: f.input_type,
          label: f.label.trim(),
          captured_by_role: f.captured_by_role || undefined,
          options_source: f.options_source || undefined,
          options_params: f.options_source === 'static'
            ? { options: f.options_value.split(',').map((v) => v.trim()).filter(Boolean).map((v) => ({ value: v, label: v })) }
            : f.options_source === 'role_lookup'
            ? { role: f.options_value }
            : {},
          mandatory: f.mandatory,
          tenant_relabel_allowed: f.tenant_relabel_allowed,
        }])),
      };
      if (onCapturedEnabled && actionKey) {
        conditionConfig.on_captured = {
          action_key: actionKey,
          input_mapping: Object.fromEntries(inputMapping.filter((m) => m.param.trim() && m.expr.trim()).map((m) => [m.param.trim(), m.expr.trim()])),
          mode,
          on_failure: onFailure,
          max_retries: maxRetries,
          backoff,
        };
      }
    }
    onAdd({
      gate_code: slug(name),
      scope: 'transition',
      target_ref: targetRef.trim(),
      classification,
      condition_type: conditionType,
      condition_config: conditionConfig,
      failure_message: failureMessage.trim() || undefined,
      description: name.trim(),
      is_optional: false,
    });
    onClose();
  };

  return (
    <Modal
      title="Add a governance condition"
      onClose={onClose}
      onSave={save}
      saveLabel="Add condition"
      saveDisabled={!canSave}
      maxWidthClassName={conditionType === 'required_data' ? 'max-w-4xl' : 'max-w-lg'}
      footerLeft={conditionType === 'required_data' ? t('workflow.settings.requiredDataBuilder.footerRecheckNote') : undefined}
    >
      <div>
        <Label required>Condition name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. QA sign-off approval" />
      </div>
      <div>
        <Label required>Applies to (transition or status code)</Label>
        <Input value={targetRef} onChange={(e) => setTargetRef(e.target.value)} placeholder="e.g. qa_inspection_to_job_completed" />
      </div>
      <div>
        <Label required>Condition type</Label>
        <Select value={conditionType} onChange={(e) => setConditionType(e.target.value)}>
          {CONDITION_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
        </Select>
      </div>
      <div>
        <Label required>Enforcement</Label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
            <input type="radio" checked={classification === 'blocking'} onChange={() => setClassification('blocking')} /> Blocking — cannot proceed
          </label>
          <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
            <input type="radio" checked={classification === 'warning'} onChange={() => setClassification('warning')} /> Warning — ack to proceed
          </label>
        </div>
      </div>
      <div>
        <Label>Failure message (shown to the user when blocked)</Label>
        <Input
          value={failureMessage}
          onChange={(e) => setFailureMessage(e.target.value)}
          placeholder="e.g. Needs QA sign-off before this can move forward"
        />
        <p className="mt-1 text-[11px] text-ui-text3">
          Leave blank to use a generic default — but a specific message here is what SAM reads back to the user
          when this move is blocked, so it&apos;s worth writing one.
        </p>
      </div>

      {conditionType === 'required_data' && (
        <RequiredDataCaptureBuilder
          captureFields={captureFields} setCaptureFields={setCaptureFields}
          onCapturedEnabled={onCapturedEnabled} setOnCapturedEnabled={setOnCapturedEnabled}
          actionKey={actionKey} setActionKey={setActionKey}
          inputMapping={inputMapping} setInputMapping={setInputMapping}
          mode={mode} setMode={setMode}
          onFailure={onFailure} setOnFailure={setOnFailure}
          maxRetries={maxRetries} setMaxRetries={setMaxRetries}
          backoff={backoff} setBackoff={setBackoff}
        />
      )}

      {conditionType === 'approval' && (
        <div className="space-y-3 border-t border-ui-border pt-3">
          <p className="text-xs font-semibold text-ui-text2">Approval settings</p>
          {approvalTypes.length > 0 && (
            <div>
              <Label>Approval type (optional)</Label>
              <Select value={approvalTypeCode} onChange={(e) => selectApprovalType(e.target.value)}>
                <option value="">Custom — set role manually below</option>
                {approvalTypes.map((a) => <option key={a.approval_type_code} value={a.approval_type_code}>{a.name}</option>)}
              </Select>
            </div>
          )}
          <div>
            <Label>Approver role</Label>
            <Select value={approverRole} onChange={(e) => setApproverRole(e.target.value)}>
              <option value="">Select…</option>
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{humanize(r)}</option>)}
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
            <input type="checkbox" checked={prohibitSelfApproval} onChange={(e) => setProhibitSelfApproval(e.target.checked)} /> Prohibit self-approval
          </label>
        </div>
      )}
    </Modal>
  );
};

// ─────────────────────────── Add Approval Type modal ──────────────────────
// WFL-CFG-PLT-03-004 — a named, reusable approval type (e.g. "Proposal Review", "QA Sign-off")
// with a default approver role. Governance conditions with condition_type='approval' reference
// one of these by code instead of re-typing an approver role on every gate.

const AddApprovalTypeModal: React.FC<{
  onClose: () => void;
  onAdd: (a: ApprovalType) => void;
}> = ({ onClose, onAdd }) => {
  const [name, setName] = useState('');
  const [approverRole, setApproverRole] = useState(ROLE_OPTIONS[0]);
  const [selfApprovalAllowed, setSelfApprovalAllowed] = useState(true);
  const [exposesCorrective, setExposesCorrective] = useState(false);
  const [description, setDescription] = useState('');

  const canSave = name.trim() && approverRole;

  const save = () => {
    if (!canSave) return;
    onAdd({
      approval_type_code: slug(name),
      name: name.trim(),
      approver_role: approverRole,
      self_approval_allowed: selfApprovalAllowed,
      exposes_corrective_on_reject: exposesCorrective,
      description: description.trim(),
    });
    onClose();
  };

  return (
    <Modal title="Add an approval type" onClose={onClose} onSave={save} saveLabel="Add approval type" saveDisabled={!canSave}>
      <div>
        <Label required>Name</Label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Proposal Review" />
      </div>
      <div>
        <Label required>Approver role</Label>
        <Select value={approverRole} onChange={(e) => setApproverRole(e.target.value)}>
          {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{humanize(r)}</option>)}
        </Select>
      </div>
      <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
        <input type="checkbox" checked={selfApprovalAllowed} onChange={(e) => setSelfApprovalAllowed(e.target.checked)} /> Self-approval allowed
      </label>
      <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
        <input type="checkbox" checked={exposesCorrective} onChange={(e) => setExposesCorrective(e.target.checked)} /> Rejection exposes a corrective transition
      </label>
      <div>
        <Label>Description (optional)</Label>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="When this approval type applies" />
      </div>
    </Modal>
  );
};

// ─────────────────────────── Add Automation Hook modal ────────────────────

const AddHookModal: React.FC<{
  approvalTypes: ApprovalType[];
  onClose: () => void;
  onAdd: (h: AutomationHook) => void;
}> = ({ approvalTypes, onClose, onAdd }) => {
  const [triggerPoint, setTriggerPoint] = useState(TRIGGER_POINTS[0].value);
  const [triggerRef, setTriggerRef] = useState('');
  const [actionType, setActionType] = useState(ACTION_TYPES[0].value);
  const [execution, setExecution] = useState<'immediate' | 'background'>('immediate');
  const [mandatory, setMandatory] = useState(false);
  const [approvalTypeCode, setApprovalTypeCode] = useState('');
  const [approverRole, setApproverRole] = useState('');

  const isApproval = actionType === 'approval_creation';
  const canSave = triggerRef.trim() && (!isApproval || approverRole);

  const save = () => {
    if (!canSave) return;
    onAdd({
      hook_code: `${slug(triggerRef)}_${actionType}`,
      trigger_point: triggerPoint,
      trigger_ref: triggerRef.trim(),
      action_type: actionType,
      action_config: isApproval
        ? { approval_type: approvalTypeCode || undefined, approver_role: approverRole, approvers: [{ role: approverRole }] }
        : { execution },
      is_mandatory: mandatory,
      is_optional: !mandatory,
      description: '',
    });
    onClose();
  };

  return (
    <Modal title="Add an automation hook" onClose={onClose} onSave={save} saveLabel="Add hook" saveDisabled={!canSave}>
      <div>
        <Label required>Trigger point</Label>
        <Select value={triggerPoint} onChange={(e) => setTriggerPoint(e.target.value)}>
          {TRIGGER_POINTS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </Select>
      </div>
      <div>
        <Label required>Bound to (status or transition code)</Label>
        <Input value={triggerRef} onChange={(e) => setTriggerRef(e.target.value)} placeholder="e.g. invoice_preparation" />
      </div>
      <div>
        <Label required>Action type — registered actions only</Label>
        <Select value={actionType} onChange={(e) => setActionType(e.target.value)}>
          {ACTION_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
        </Select>
      </div>

      {isApproval ? (
        <div className="space-y-3 border-t border-ui-border pt-3">
          <p className="text-xs font-semibold text-ui-text2">Approval settings</p>
          <p className="text-[11px] text-ui-text3">
            This is what actually creates the pending approval request when the trigger fires — without this, a
            governance gate referencing this approval type will block forever with nothing to approve.
          </p>
          {approvalTypes.length > 0 && (
            <div>
              <Label>Approval type (optional)</Label>
              <Select value={approvalTypeCode} onChange={(e) => setApprovalTypeCode(e.target.value)}>
                <option value="">Custom — set role manually below</option>
                {approvalTypes.map((a) => <option key={a.approval_type_code} value={a.approval_type_code}>{a.name}</option>)}
              </Select>
            </div>
          )}
          <div>
            <Label required>Approver role</Label>
            <Select value={approverRole} onChange={(e) => setApproverRole(e.target.value)}>
              <option value="">Select…</option>
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{humanize(r)}</option>)}
            </Select>
          </div>
        </div>
      ) : (
        <div>
          <Label>Execution</Label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
              <input type="radio" checked={execution === 'immediate'} onChange={() => setExecution('immediate')} /> Immediate — inline post-commit
            </label>
            <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
              <input type="radio" checked={execution === 'background'} onChange={() => setExecution('background')} /> Background → SBP, retries
            </label>
          </div>
        </div>
      )}

      <label className="flex items-center gap-2 text-sm text-ui-text2 cursor-pointer">
        <input type="checkbox" checked={mandatory} onChange={(e) => setMandatory(e.target.checked)} /> Mandatory (must succeed to finalize)
      </label>
      <p className="text-xs text-ui-text3">
        WFL records a minimum outcome (action · occurrence · success/fail). No tenant scripts, formulas, or external API calls — execution is owned by the target module & SBP.
      </p>
    </Modal>
  );
};

// ─────────────────────────── Main page ────────────────────────────────────

const WorkflowGovernancePage: React.FC = () => {
  const { definitionId, version } = useParams<{ definitionId: string; version: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useGetWorkflowDefinitionQuery({ definition_id: definitionId!, version: Number(version) });
  const [updateDraft, { isLoading: saving }] = useUpdateWorkflowDefinitionMutation();

  const [gates, setGates] = useState<GovernanceGate[]>([]);
  const [approvalTypes, setApprovalTypes] = useState<ApprovalType[]>([]);
  const [hooks, setHooks] = useState<AutomationHook[]>([]);
  const [showAddGate, setShowAddGate] = useState(false);
  const [showAddApprovalType, setShowAddApprovalType] = useState(false);
  const [showAddHook, setShowAddHook] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!data?.data) return;
    setGates(data.data.governance_gates as GovernanceGate[]);
    setApprovalTypes((data.data.approval_types ?? []) as ApprovalType[]);
    setHooks(data.data.automation_hooks as AutomationHook[]);
  }, [data]);

  const def = data?.data;
  const isDraft = def?.status === 'draft';

  const handleSave = async () => {
    if (!def) return;
    setError('');
    try {
      const body: CreateWorkflowRequest = {
        name: def.name,
        description: def.description,
        engagement_types: def.engagement_types,
        stages: def.stages,
        processes: def.processes,
        statuses: def.statuses,
        entry_points: def.entry_points,
        transitions: def.transitions,
        governance_gates: gates,
        approval_types: approvalTypes,
        automation_hooks: hooks,
        authority_rules: def.authority_rules,
      };
      await updateDraft({ definition_id: def.definition_id, version: def.version, body }).unwrap();
    } catch (e: unknown) {
      const err = e as { data?: { message?: string } };
      setError(err?.data?.message ?? 'Failed to save.');
    }
  };

  if (isLoading || !def) {
    return <PageShell header={null}><div className="py-20 text-center text-ui-text3">Loading…</div></PageShell>;
  }

  return (
    <PageShell
      maxWidth="max-w-6xl"
      header={
        <div className="flex items-start justify-between">
          <div>
            <button onClick={() => navigate(`/platform/workflows/${definitionId}/${version}/edit`)} className="text-xs text-ui-text3 hover:text-brand-dark mb-1">← Back to builder</button>
            <p className="text-xs text-ui-text3">{def.name} /</p>
            <h1 className="text-xl font-bold text-ui-text mt-0.5">Governance & Automation Hooks</h1>
          </div>
          <div className="flex items-center gap-2">
            {error && <p className="text-xs text-red-400">{error}</p>}
            {isDraft && <Btn variant="primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</Btn>}
          </div>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* ── Left column: Governance conditions + Approval Types ─────────── */}
        <div className="space-y-8">
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-ui-text">Governance conditions</h2>
                <span className="px-2 py-0.5 rounded-full text-xs bg-ui-subtle text-ui-text2">{gates.length}</span>
              </div>
              {isDraft && <Btn variant="primary" onClick={() => setShowAddGate(true)}>+ Add condition</Btn>}
            </div>
            <div className="space-y-1.5">
              {gates.map((g) => (
                <div key={g.gate_code} className="flex items-center justify-between rounded-lg border border-ui-border2 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm text-ui-text truncate">{g.description || g.gate_code}</p>
                    <p className="text-xs text-ui-text3">on {g.target_ref} · type: {g.condition_type.replace(/_/g, ' ')}</p>
                  </div>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase shrink-0 ${g.classification === 'blocking' ? 'bg-red-500/10 text-red-400' : 'bg-yellow-500/10 text-yellow-400'}`}>{g.classification}</span>
                </div>
              ))}
              {gates.length === 0 && <p className="text-sm text-ui-text3 py-4 text-center border border-dashed border-ui-border2 rounded-lg">No governance conditions yet.</p>}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-ui-text">Approval types</h2>
                <span className="px-2 py-0.5 rounded-full text-xs bg-ui-subtle text-ui-text2">{approvalTypes.length}</span>
              </div>
              {isDraft && <Btn variant="primary" onClick={() => setShowAddApprovalType(true)}>+ Add approval type</Btn>}
            </div>
            <div className="space-y-1.5">
              {approvalTypes.map((a) => (
                <div key={a.approval_type_code} className="flex items-center justify-between rounded-lg border border-ui-border2 px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm text-ui-text truncate">{a.name}</p>
                    <p className="text-xs text-ui-text3">approver role: {humanize(a.approver_role)}{a.exposes_corrective_on_reject ? ' · reject → exposes corrective transition' : ''}</p>
                  </div>
                  {!a.self_approval_allowed && <span className="px-1.5 py-0.5 rounded text-[10px] uppercase shrink-0 bg-ui-subtle text-ui-text2">No self-approval</span>}
                </div>
              ))}
              {approvalTypes.length === 0 && <p className="text-sm text-ui-text3 py-4 text-center border border-dashed border-ui-border2 rounded-lg">No approval types yet.</p>}
            </div>
          </div>
        </div>

        {/* ── Right column: Automation hooks ───────────────────────────────── */}
        <div>
          <p className="text-xs text-ui-text3 mb-3">Predefined actions only · background & scheduled → SBP</p>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-ui-text">Automation hooks</h2>
              <span className="px-2 py-0.5 rounded-full text-xs bg-ui-subtle text-ui-text2">{hooks.length}</span>
            </div>
            {isDraft && <Btn variant="primary" onClick={() => setShowAddHook(true)}>+ Add hook</Btn>}
          </div>
          <div className="space-y-1.5">
            {hooks.map((h) => (
              <div key={h.hook_code} className="flex items-center justify-between rounded-lg border border-ui-border2 px-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-sm text-ui-text truncate">{ACTION_TYPES.find((a) => a.value === h.action_type)?.label ?? h.action_type}</p>
                  <p className="text-xs text-ui-text3">{h.trigger_point.replace(/_/g, ' ')} · {h.trigger_ref}</p>
                </div>
                {h.is_mandatory && <span className="px-1.5 py-0.5 rounded text-[10px] uppercase shrink-0 bg-ui-subtle text-ui-text2">Mandatory</span>}
              </div>
            ))}
            {hooks.length === 0 && <p className="text-sm text-ui-text3 py-4 text-center border border-dashed border-ui-border2 rounded-lg">No automation hooks yet.</p>}
          </div>
          <p className="text-xs text-ui-text3 mt-3">No tenant scripts, formulas, or external API calls. WFL keeps a minimum outcome record; execution owned by SBP.</p>
        </div>
      </div>

      {showAddGate && <AddGateModal approvalTypes={approvalTypes} onClose={() => setShowAddGate(false)} onAdd={(g) => setGates((prev) => [...prev, g])} />}
      {showAddApprovalType && <AddApprovalTypeModal onClose={() => setShowAddApprovalType(false)} onAdd={(a) => setApprovalTypes((prev) => [...prev, a])} />}
      {showAddHook && <AddHookModal approvalTypes={approvalTypes} onClose={() => setShowAddHook(false)} onAdd={(h) => setHooks((prev) => [...prev, h])} />}
    </PageShell>
  );
};

export default WorkflowGovernancePage;
